import type { Supply48DataSource } from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  Box,
  CloseButton,
  Flex,
  LightModeSemanticColors,
  Text,
} from '@enechain/ecloud-designsystem'

import { ChartTooltip } from '~/components/common/ChartTooltip'
import { KOMA_TIME_LABELS } from '~/models/komaToTime'
import { CHART_DEFAULT_WIDTH } from '~/pages/Supply48Data/hooks/useResizableChartWidth'
import type { DisplayUnit } from '~/pages/Supply48Data/models/displayUnit'
import {
  displayUnitLabelKey,
  toDisplayValue,
} from '~/pages/Supply48Data/models/displayUnit'
import type { EditStateMap } from '~/pages/Supply48Data/models/editState'
import { makeEditRowKey } from '~/pages/Supply48Data/models/editState'
import { displaySlotValue } from '~/pages/Supply48Data/models/gridRows'
import type { DayIndex } from '~/pages/Supply48Data/models/saveRows'
import { sourceDisplayLabel } from '~/pages/Supply48Data/models/sourceDisplayLabel'

type Props = {
  /** 表示対象の Leg (source) */
  legs: Supply48DataSource[]
  /**
   * フィルタで選択中の受渡日 (= 期間の起点 dateFrom)。
   * グラフはこの日の 48 コマを描画する (leg.days の並び順には依存しない)。
   */
  selectedDate: ProtoDate | undefined
  /** §2: dayIndex は page で 1 回だけ useMemo 構築して渡す */
  dayIndex: DayIndex
  /** 現在の編集状態 (編集中の値をグラフに反映する) */
  editState: EditStateMap
  /**
   * §8: グラフ対象の legId。
   * legs 構成変化で別 leg を黙って表示する事故を防ぐため index ではなく id で管理。
   */
  targetLegId: string
  /** 表示単位 (AC-S4D-037/038)。値の換算と Y 軸ラベルに使う */
  displayUnit: DisplayUnit
  /** 閉じるボタンのコールバック */
  onClose: () => void
  /**
   * パネル幅 (px)。 `PanelResizer` で可変にするため page が持つ
   * (proto `supply/48data.html` の CSS 変数 `--s48-chart-w` に対応。 既定 360px)。
   */
  width?: number
}

type ChartEntry = {
  koma: string
  value: number
}

/**
 * 48 コマ値の推移グラフパネル。
 *
 * - 列ヘッダクリックで対象列を切替 (targetLegId で制御)
 * - Recharts BarChart を使用
 * - 固定幅 360px
 */
export const Supply48DataChart = ({
  legs,
  selectedDate,
  dayIndex,
  editState,
  targetLegId,
  displayUnit,
  onClose,
  width = CHART_DEFAULT_WIDTH,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // §8: legId (source id) で resolve。失敗時は noData 表示
  const leg = legs.find((l) => l.supply48SourceId === targetLegId)

  // 各コマの表示値を組み立てる (編集中の値があれば優先)。
  // 描画対象は「選択中の受渡日 (selectedDate)」の 48 コマ。leg.days の並び順や
  // データ有無に依存せず、選択日を rowKey にして dayIndex / editState を引く。
  const chartData: ChartEntry[] = (() => {
    if (leg === undefined || selectedDate === undefined) return []

    const rowKey = makeEditRowKey(
      leg.supply48SourceId,
      selectedDate.year,
      selectedDate.month,
      selectedDate.day,
    )
    const editRow = editState.get(rowKey)
    // §9: dayIndex 経由で選択日の day を取得 (Chart は dayIndex prop を受け取る)
    const dayFromIndex = dayIndex.get(rowKey)

    return Array.from({ length: 48 }, (_, i) => {
      // §9 Chart/Grid 共通 displaySlotValue 純関数を使用
      const stored = displaySlotValue(dayFromIndex, editRow?.slots, i)
      // AC-S4D-037: グラフもグリッドと同じ表示単位で描く (保存値は kWh/30min 固定)
      const val = toDisplayValue(stored ?? null, displayUnit)
      return {
        koma: KOMA_TIME_LABELS[i],
        value: val ?? 0,
      }
    })
  })()

  // 軸・グリッド色 (raw hex: eclair tokens)
  const axisColor = LightModeSemanticColors.Text.midEmphasis.value
  const gridColor = LightModeSemanticColors.Border.lowEmphasis.value
  const barColor = LightModeSemanticColors.Surface.accentPrimary.value

  // AC-S4D-004/040: 表示名導出は sourceDisplayLabel に集約する (契約=取引先名
  // + 取引ID / 市場=market_kind ラベル)。市場列をグラフ対象にした時に
  // 見出し・系列名が「—」に落ちていた抜け漏れの修正 (旧実装は contract 分岐の
  // みで市場 variant を想定していなかった)。leg 未解決時に加え、
  // sourceDisplayLabel が未対応 variant (将来の source type 等) で空文字を
  // 返した場合も、見出し・系列名が空になる退行を避けるため '—' にフォールバックする。
  const resolvedLabel =
    leg !== undefined
      ? sourceDisplayLabel(leg, t, { includeTradeId: true })
      : ''
  const legLabel = resolvedLabel !== '' ? resolvedLabel : '—'

  return (
    // 8080 `.s48-chart-panel { display:flex; flex-direction:column }` +
    // `.s48-chart-body { flex:1; min-height:320px }` と同じで、 グラフ本体が
    // 残り高さいっぱいに伸びるようにする (デザイナー指摘 2026-08-28
    // 「グラフの height をブラウザいっぱいに」)。 固定 320px だと画面が高くても
    // グラフだけ小さいままになる。
    //
    // **高さは指定しない**。 親 (index.tsx の Flex) が `alignItems="stretch"` なので、
    // 何も書かなければ行の高さ (= 表の `maxHeight: calc(100vh - 240px)`) まで伸びる。
    // ここに `height="100%"` を書くと親が高さ未指定で 100% を解決できず `auto` に落ち、
    // **stretch を打ち消して**内容高さ (ヘッダ + minHeight 320px) で止まる
    // (Chromium 実測: viewport 900px で 662px → 370px)。
    <Box
      flexDirection="column"
      flexShrink={0}
      display="flex"
      overflow="hidden"
      // 幅は #6602 のリサイザーが決める (props の `width`)。 本 PR は当初
      // 360px 固定にしていたが、 その間に #6602 (表 / グラフ間のリサイザー) が
      // main へ入ったため、 固定に戻すとリサイザーが効かなくなる。 幅は可変のまま、
      // 本 PR の目的である**高さ**だけを扱う。
      width={`${width}px`}
      background="Surface.primary"
      borderWidth={1}
      borderColor="Border.lowEmphasis"
      // 器は UI 辞書 card.md の `.card` (8080 `card s48-chart-panel`):
      // 角丸 lg + 影 shadow-sm。 見出しは `.card-header` = 余白 16px・地なし・下線
      borderRadius="lg"
      boxShadow="shadSm"
    >
      {/* ヘッダ */}
      <Flex
        alignItems="center"
        gap={2}
        padding={4}
        borderColor="Border.lowEmphasis"
        borderBottomWidth={1}
      >
        {/* design `.card-header` = lg/600。 2026-07-12 に「compact 表示が意図の
            可能性」 として是正を見送っていたが、 2026-08-28 デザイナー判断
            「card header UI も 8080 準拠」 で是正する。 role は sectionTitle
            (lg/semibold = `~/constants/typography.ts`)。 */}
        <Text
          textStyle="sectionTitle"
          flex={1}
          overflow="hidden"
          color="Text.highEmphasis"
          style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {t('page:supply48Data.chart.title')}: <strong>{legLabel}</strong>
        </Text>
        <CloseButton
          aria-label={t('page:supply48Data.chart.close')}
          onClick={onClose}
          size="sm"
        />
      </Flex>

      {/*
       * グラフ本体。
       * css で recharts の目盛テキストを 9px に固定する: ecloud/Chakra の
       * グローバル `:where(svg){font-size:1.2em}` により recharts の <svg> 配下が
       * 14px を継承し、Recharts の `tick={{ fontSize: 9 }}`(= presentation 属性)
       * が継承値に負けて効かない。class セレクタで直接上書きして design mock
       * (9px) と揃える (軸ラベルの重なり = 横軸崩れの是正)。
       */}
      <Box
        flex={1}
        minHeight="320px"
        padding={2}
        css={{ '& .recharts-text': { fontSize: '9px' } }}
      >
        {chartData.length === 0 ? (
          <Flex alignItems="center" justifyContent="center" height="100%">
            <Text color="Text.midEmphasis" fontSize="sm">
              {t('page:supply48Data.chart.noData')}
            </Text>
          </Flex>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              /* top は Y 軸の単位ラベル (AC-S4D-038) の分だけ確保する */
              margin={{ bottom: 4, left: 0, right: 8, top: 20 }}
              data={chartData}
            >
              <CartesianGrid
                stroke={gridColor}
                strokeDasharray="3 3"
                vertical={false}
              />
              <XAxis
                dataKey="koma"
                interval={5}
                tick={{ fill: axisColor, fontSize: 9 }}
              />
              {/*
               * 48 点データは kW 実量 (5〜6 桁) なので桁区切り整形する (需要想定
               * ForecastChart 等の大きい値を扱う既存 chart と同方針)。design mock
               * (0〜360 の小さいサンプル値) より桁数が多いぶん軸幅を広げて、
               * 目盛ラベルが軸幅外に切れないようにする。
               */}
              <YAxis
                width={48}
                allowDecimals={false}
                tick={{ fill: axisColor, fontSize: 9 }}
                tickFormatter={(v: number) => Math.round(v).toLocaleString()}
              >
                {/* AC-S4D-038: 現在の表示単位を軸ラベルに明示する */}
                <Label
                  position="top"
                  fill={axisColor}
                  fontSize={9}
                  value={t(displayUnitLabelKey(displayUnit))}
                />
              </YAxis>
              {/* tooltip は全画面共通の見た目 (design js/charts.js の
                  darkTooltipContent 移植)。 8080 も 48点データで
                  `content: darkTooltipContent` + `offset: 16` を渡している
                  (design/supply/48data.html:2398)。 */}
              <Tooltip content={<ChartTooltip />} offset={16} />
              <Bar fill={barColor} dataKey="value" name={legLabel} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  )
}
